import type { PixelFrame } from "pixelfx";
import type { HeroAtlas } from "./sprites/atlas";

/**
 * Скачок — превращение в пыльцу (Джон 04.11.2026: «чтобы персонаж трансформировался в эту пыльцу»): пиксели кадра
 * бойца становятся частицами своего цвета, облако летит дугой и крутится вокруг вертикальной оси, к концу каждый
 * пиксель приходит в своё зеркальное место — боец собирается уже лицом к сопернику. Как в образце монтажёра
 * («облако складывается в форму»): частица идёт туда, куда её несёт вращение, форма растёт из пятна, а не из каши.
 * Всё — от кадра способности и положений мира: при откате и в записи картинка та же.
 */
export interface CloudFlight {
  /** Доля полёта 0..1. */
  readonly progress: number;
  /** Стопы в начале и в конце, пиксели экрана арта. */
  readonly fromX: number;
  readonly toX: number;
  readonly feet: number;
  /** Куда смотрел в начале (в конце — наоборот). */
  readonly facing: number;
  readonly side: number;
}

/** Высота дуги и разлёт облака, пиксели арта. */
const ARC = 175;
const SPREAD = 26;
const CENTER = 95;

const hash = (value: number): number => {
  let h = Math.imul(value ^ 0x9e3779b9, 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return (h ^ (h >>> 16)) >>> 0;
};

/** Центр облака по доле полёта: прямая между стопами плюс дуга вверх. Камера едет за ним. */
export function cloudCenterX(flight: CloudFlight): number {
  return flight.fromX + (flight.toX - flight.fromX) * across(ease(flight.progress));
}

/** Фиолетовый свет в точку (ABGR, насыщение). */
function glowAt(frame: PixelFrame, x: number, y: number, glow: number): void {
  if (x < 0 || y < 0 || x >= frame.w || y >= frame.h || glow <= 0) return;
  const i = y * frame.w + x;
  const old = frame.light[i] ?? 0;
  const r = Math.min(255, (old & 255) + glow);
  const g = Math.min(255, ((old >>> 8) & 255) + Math.round(glow * 0.55));
  const b = Math.min(255, ((old >>> 16) & 255) + glow);
  frame.light[i] = ((255 << 24) | (b << 16) | (g << 8) | r) >>> 0;
}

/**
 * Замах скачка — видимое предупреждение для контрприёма: боец ещё стоит, а с него начинают сыпаться искры его же цвета
 * (u 0..1 — доля замаха). Рисуется поверх спрайта.
 */
export function drawShedding(
  frame: PixelFrame,
  atlas: HeroAtlas,
  x: number,
  feet: number,
  facing: number,
  side: number,
  u: number,
): void {
  const clip = atlas.get("fighting-idle");
  const sprite = clip?.frames[0];
  if (!clip || !sprite) return;
  let particle = 0;
  for (let row = 0; row < sprite.h; row += 2) {
    for (let column = 0; column < sprite.w; column += 2) {
      const color = clip.pixels[(sprite.y + row) * clip.width + sprite.x + column] ?? 0;
      if (color >>> 24 < 128) continue;
      particle++;
      const seed = hash(particle * 40503 + side * 7);
      if ((seed & 255) / 255 > u * 0.9) continue; // сыплется всё больше
      const drift = u * (10 + ((seed >>> 8) & 31));
      const px = Math.round(x + (sprite.ox + column) * facing + Math.cos(seed) * drift);
      const py = Math.round(feet + sprite.oy + row - drift * 0.6 + u * u * ((seed >>> 16) & 7));
      if (px < 0 || py < 0 || px >= frame.w || py >= frame.h) continue;
      frame.color[py * frame.w + px] = color;
      glowAt(frame, px, py, Math.round(90 + 100 * u));
      glowAt(frame, px, py + 1, Math.round(40 + 50 * u));
    }
  }
}

const ease = (u: number): number => (u <= 0 ? 0 : u >= 1 ? 1 : u * u * (3 - 2 * u));
/** По горизонтали облако приходит раньше, чем снижается: спуск — уже за спиной соперника, а не сквозь него. */
const across = (e: number): number => 1 - Math.pow(1 - e, 3);

/** Нарисовать облако-бойца: кадр стойки из атласа, каждая непрозрачная точка — частица. */
export function drawTeleportCloud(frame: PixelFrame, atlas: HeroAtlas, flight: CloudFlight): void {
  const clip = atlas.get("fighting-idle");
  const sprite = clip?.frames[0];
  if (!clip || !sprite) return;
  const centerY = flight.feet - CENTER;
  const index = (x: number, y: number): number => y * frame.w + x;
  let particle = 0;
  for (let row = 0; row < sprite.h; row++) {
    for (let column = 0; column < sprite.w; column++) {
      const color = clip.pixels[(sprite.y + row) * clip.width + sprite.x + column] ?? 0;
      if (color >>> 24 < 128) continue;
      particle++;
      const seed = hash(particle * 2654435761 + flight.side);
      // свой сдвиг по времени: облако рассыпается и собирается волной, а не разом
      const lag = ((seed & 255) / 255) * 0.25;
      const u = Math.min(1, Math.max(0, (flight.progress - lag * (1 - flight.progress)) / (1 - lag * 0.5)));
      const e = ease(u);
      const swirl = Math.sin(Math.PI * e);
      // точка относительно центра тела, лицом как в начале
      const dx = (sprite.ox + column + 0.5) * flight.facing;
      const dy = sprite.oy + row + CENTER;
      // вращение вокруг вертикальной оси: к концу x зеркален — боец лицом к сопернику
      const spin = Math.cos(Math.PI * e);
      const scatterAngle = ((seed >>> 8) & 1023) * 0.00614;
      const scatter = swirl * SPREAD * (0.4 + ((seed >>> 18) & 255) / 255);
      const cx = flight.fromX + (flight.toX - flight.fromX) * across(e);
      const cy = centerY - swirl * ARC;
      const contract = 1 - 0.55 * swirl;
      const x = Math.round(cx + dx * spin * contract + Math.cos(scatterAngle + e * 6) * scatter);
      const y = Math.round(cy + dy * contract + Math.sin(scatterAngle + e * 6) * scatter * 0.6);
      if (x < 0 || y < 0 || x >= frame.w || y >= frame.h) continue;
      // хвост: та же частица чуть раньше по пути — светом, тусклее
      if (swirl > 0.2) {
        const back = Math.max(0, e - 0.14);
        const tx = Math.round(
          flight.fromX + (flight.toX - flight.fromX) * across(back) + dx * Math.cos(Math.PI * back),
        );
        const ty = Math.round(centerY - Math.sin(Math.PI * back) * ARC + dy * (1 - 0.55 * Math.sin(Math.PI * back)));
        glowAt(frame, tx, ty, Math.round(swirl * 50));
      }
      // в полёте частица — цвет пикселя с фиолетовым светом; в начале и в конце — почти обычный пиксель
      frame.color[index(x, y)] = color;
      if (swirl > 0.15) {
        const glow = Math.round(swirl * 80);
        const old = frame.light[index(x, y)] ?? 0;
        const r = Math.min(255, (old & 255) + glow);
        const g = Math.min(255, ((old >>> 8) & 255) + Math.round(glow * 0.55));
        const b = Math.min(255, ((old >>> 16) & 255) + glow);
        frame.light[index(x, y)] = ((255 << 24) | (b << 16) | (g << 8) | r) >>> 0;
      }
    }
  }
}
